import onetime from "@-/com.npmjs.onetime";
import { AppState, Platform } from "@-/com.npmjs.react-native";
import { addNetworkStateListener } from "@-/com.npmjs.expo-network";
import { subscribeToSentryEvents } from "@-/dev.improve.haniverse.lib.sentry";
import { animationFrames } from "@-/dev.improve.haniverse.lib.scheduler";
import {
  type NativeProcessStatsSnapshot,
  nativeProcessStats,
} from "@-/dev.improve.haniverse.modules.process-stats";
import { deriveProcessStatsSample } from "#derive-process-stats-sample";
import {
  rotateDiagnosticsSessionFile,
  writeDiagnosticsSessionFile,
} from "#diagnostics-session-file";
import {
  getDiagnosticsLastProcessSnapshot,
  getDiagnosticsRoute,
  isDiagnosticsRecording,
  markDiagnosticsRecording,
  recordDiagnosticsEvent,
  recordDiagnosticsLog,
  recordDiagnosticsSample,
  setDiagnosticsLastProcessSnapshot,
  snapshotDiagnostics,
} from "#diagnostics-store";
import { formatLogArguments } from "#format-log-arguments";
import { subscribeToCapturedLogs } from "#subscribe-to-captured-logs";
import { readJsHeapStats } from "#js-runtime-stats";

// How often a resource sample is taken. Two seconds keeps ~80 minutes in the sample buffer and
// costs one sub-millisecond native call per tick.
export const DIAGNOSTICS_SAMPLE_INTERVAL_MS = 2000;
/** Threads kept per sample (hottest first). */
const THREAD_LIMIT = 12;
// The JS stall detector: a 250 ms timer that measures how late it fires. Anything beyond
// 200 ms of lag means the JS thread was busy (or blocked) for that long.
const STALL_PROBE_INTERVAL_MS = 250;
const STALL_THRESHOLD_MS = 200;
// Frames longer than this (at 60 Hz, three missed vsyncs) count as long frames.
const LONG_FRAME_MS = 50;
// The on-disk session snapshot: rewritten this often and whenever the app leaves the foreground,
// bounded so a write stays a few hundred KB.
const SESSION_FLUSH_INTERVAL_MS = 30_000;
const SESSION_FILE_LIMITS = { events: 500, logs: 1500, samples: 900 } as const;

type ErrorUtilsShape = {
  getGlobalHandler?: () => (error: unknown, isFatal?: boolean) => void;
  setGlobalHandler?: (handler: (error: unknown, isFatal?: boolean) => void) => void;
};

type RejectionTrackerShape = {
  enablePromiseRejectionTracker?: (options: {
    allRejections: boolean;
    onHandled: (id: number) => void;
    onUnhandled: (id: number, rejection: unknown) => void;
  }) => void;
};

type RecorderState = {
  disposers: (() => void)[];
  hasRotatedSessionFile: boolean;
  isActive: boolean;
};

const getRecorderState = onetime((): RecorderState => ({
  disposers: [],
  hasRotatedSessionFile: false,
  isActive: false,
}));

function installGlobalErrorCapture(): () => void {
  const errorUtils: unknown = Reflect.get(globalThis, "ErrorUtils");
  if (errorUtils === null || typeof errorUtils !== "object") {
    return () => {};
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- shape checked below
  const utils = errorUtils as ErrorUtilsShape;
  if (utils.getGlobalHandler === undefined || utils.setGlobalHandler === undefined) {
    return () => {};
  }
  const previous = utils.getGlobalHandler();
  const handler = (error: unknown, isFatal?: boolean): void => {
    try {
      const formatted = formatLogArguments([error]);
      recordDiagnosticsEvent({
        fatal: isFatal === true,
        kind: "error",
        message: formatted.error ?? formatted.message,
        name: error instanceof Error ? error.name : "Error",
        stack: error instanceof Error ? (error.stack ?? null) : null,
        timestamp: Date.now(),
      });
      // A fatal error is about to tear the app down: persist the tail NOW.
      if (isFatal === true) {
        flushSessionFile();
      }
    } catch {
      // Never interfere with the real handler.
    }
    previous(error, isFatal);
  };
  utils.setGlobalHandler(handler);
  return () => {
    if (utils.getGlobalHandler?.() === handler) {
      utils.setGlobalHandler?.(previous);
    }
  };
}

/**
 * Hermes-only unhandled-rejection capture. React Native already enables its own tracker in dev
 * (with console warnings, which the console capture records), and Hermes has no "disable" API —
 * so this installs only in release builds and checks the recorder flag on every callback.
 */
function installUnhandledRejectionCapture(): () => void {
  if (__DEV__) {
    return () => {};
  }
  const hermes: unknown = Reflect.get(globalThis, "HermesInternal");
  if (hermes === null || typeof hermes !== "object") {
    return () => {};
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- shape checked below
  const tracker = hermes as RejectionTrackerShape;
  if (tracker.enablePromiseRejectionTracker === undefined) {
    return () => {};
  }
  try {
    tracker.enablePromiseRejectionTracker({
      allRejections: true,
      onHandled: () => {},
      onUnhandled: (_id, rejection) => {
        if (!getRecorderState().isActive) {
          return;
        }
        const formatted = formatLogArguments([rejection]);
        recordDiagnosticsEvent({
          kind: "unhandled-rejection",
          message: formatted.error ?? formatted.message,
          stack: rejection instanceof Error ? (rejection.stack ?? null) : null,
          timestamp: Date.now(),
        });
      },
    });
  } catch {
    // Older Hermes without the tracker: nothing to capture.
  }
  return () => {};
}

function installAppStateCapture(): () => void {
  const subscription = AppState.addEventListener("change", (state) => {
    recordDiagnosticsEvent({ kind: "app-state", state, timestamp: Date.now() });
    if (state !== "active") {
      flushSessionFile();
    }
  });
  return () => {
    subscription.remove();
  };
}

function installNetworkCapture(): () => void {
  try {
    const subscription = addNetworkStateListener((event) => {
      recordDiagnosticsEvent({
        isConnected: event.isConnected ?? null,
        isInternetReachable: event.isInternetReachable ?? null,
        kind: "network",
        timestamp: Date.now(),
        type: event.type ?? null,
      });
    });
    return () => {
      subscription.remove();
    };
  } catch {
    // expo-network is unavailable in some runtimes (SSR, tests); network events are optional.
    return () => {};
  }
}

function installNativeMonitoring(): () => void {
  const native = nativeProcessStats;
  if (native === null) {
    return () => {};
  }
  try {
    native.startMonitoring();
  } catch {
    return () => {};
  }
  const memoryWarning = native.addListener("onMemoryWarning", (event) => {
    recordDiagnosticsEvent({
      kind: "memory-warning",
      level: event.level,
      rawLevel: event.rawLevel,
      timestamp: event.timestampMs,
    });
    // Memory pressure often precedes a jetsam kill: get the tail on disk while we still can.
    flushSessionFile();
  });
  const thermal = native.addListener("onThermalStateChange", (event) => {
    recordDiagnosticsEvent({
      kind: "thermal",
      thermalState: event.thermalState,
      timestamp: event.timestampMs,
    });
  });
  return () => {
    memoryWarning.remove();
    thermal.remove();
    try {
      native.stopMonitoring();
    } catch {
      // Best-effort teardown.
    }
  };
}

function installSentryCapture(): () => void {
  return subscribeToSentryEvents((summary) => {
    recordDiagnosticsEvent({
      eventId: summary.eventId,
      exceptionType: summary.exceptionType,
      exceptionValue: summary.exceptionValue,
      kind: "sentry-event",
      level: summary.level,
      message: summary.message,
      timestamp: summary.timestamp,
    });
  });
}

/** Counts JS-thread frames between samples without touching the UI (no re-renders, no native props). */
function installFrameCounter(): {
  dispose: () => void;
  drain: () => { count: number; longFrames: number; maxFrameMs: number };
} {
  let count = 0;
  let longFrames = 0;
  let maxFrameMs = 0;
  let lastFrameAt: number | null = null;
  let isDisposed = false;
  const countFrames = async () => {
    for await (const frameTime of animationFrames()) {
      if (isDisposed) {
        return;
      }
      if (lastFrameAt !== null) {
        const delta = frameTime - lastFrameAt;
        count += 1;
        if (delta > LONG_FRAME_MS) {
          longFrames += 1;
        }
        maxFrameMs = Math.max(maxFrameMs, delta);
      }
      lastFrameAt = frameTime;
    }
  };
  void countFrames();
  // The frame clock stops while backgrounded; a resumed frame would otherwise look like a
  // multi-second frame.
  const appState = AppState.addEventListener("change", () => {
    lastFrameAt = null;
  });
  return {
    dispose: () => {
      appState.remove();
      isDisposed = true;
    },
    drain: () => {
      const drained = { count, longFrames, maxFrameMs: Math.round(maxFrameMs * 10) / 10 };
      count = 0;
      longFrames = 0;
      maxFrameMs = 0;
      return drained;
    },
  };
}

function readNativeSnapshot(): NativeProcessStatsSnapshot | null {
  if (nativeProcessStats === null) {
    return null;
  }
  try {
    return nativeProcessStats.getSnapshot(THREAD_LIMIT);
  } catch {
    return null;
  }
}

function installSampler(): () => void {
  const frames = installFrameCounter();
  let expectedAt = Date.now() + DIAGNOSTICS_SAMPLE_INTERVAL_MS;
  const sample = (): void => {
    const now = Date.now();
    const lag = Math.max(0, now - expectedAt);
    expectedAt = now + DIAGNOSTICS_SAMPLE_INTERVAL_MS;
    try {
      const snapshot = readNativeSnapshot();
      const previous = getDiagnosticsLastProcessSnapshot();
      recordDiagnosticsSample({
        appState: AppState.currentState ?? "unknown",
        eventLoopLagMs: lag,
        jsFrames: frames.drain(),
        jsHeap: readJsHeapStats(),
        process: snapshot === null ? null : deriveProcessStatsSample(snapshot, previous),
        route: getDiagnosticsRoute(),
        timestamp: now,
      });
      if (snapshot !== null) {
        setDiagnosticsLastProcessSnapshot(snapshot);
      }
    } catch {
      // A failed sample is skipped, never fatal.
    }
  };
  // Prime the CPU delta so the first real sample has a baseline.
  setDiagnosticsLastProcessSnapshot(readNativeSnapshot());
  const timer = setInterval(sample, DIAGNOSTICS_SAMPLE_INTERVAL_MS);
  return () => {
    clearInterval(timer);
    frames.dispose();
  };
}

function installStallProbe(): () => void {
  let expectedAt = Date.now() + STALL_PROBE_INTERVAL_MS;
  const timer = setInterval(() => {
    const now = Date.now();
    const lag = now - expectedAt;
    expectedAt = now + STALL_PROBE_INTERVAL_MS;
    if (lag > STALL_THRESHOLD_MS && AppState.currentState === "active") {
      recordDiagnosticsEvent({
        durationMs: lag,
        kind: "js-stall",
        route: getDiagnosticsRoute(),
        timestamp: now - lag,
      });
    }
  }, STALL_PROBE_INTERVAL_MS);
  return () => {
    clearInterval(timer);
  };
}

/** Discrete input only: a hover or scroll stream would flood the event buffer. */
const INTERACTION_EVENT_NAMES = new Set([
  "click",
  "keydown",
  "keyup",
  "pointerdown",
  "pointerup",
  "touchend",
  "touchstart",
]);
/** The Event Timing API's own floor; anything faster is within one 60 Hz frame. */
const INTERACTION_DURATION_THRESHOLD_MS = 16;

type PerformanceTimelineEntry = {
  duration: number;
  entryType: string;
  interactionId?: number;
  name: string;
  processingEnd?: number;
  processingStart?: number;
  startTime: number;
};
type PerformanceObserverShape = {
  disconnect: () => void;
  observe: (options: { buffered?: boolean; durationThreshold?: number; type: string }) => void;
};
type PerformanceObserverConstructor = {
  new (
    callback: (list: { getEntries: () => PerformanceTimelineEntry[] }) => void,
  ): PerformanceObserverShape;
  supportedEntryTypes?: readonly string[];
};

/**
 * Input-to-response latency and long JS tasks, measured by React Native's own performance
 * timeline: the frame counter only sees gaps between frames, not which input waited or how long
 * a response took to reach the screen.
 */
function installPerformanceTimelineCapture(): () => void {
  const Observer: unknown = Reflect.get(globalThis, "PerformanceObserver");
  if (typeof Observer !== "function" || typeof performance?.now !== "function") {
    return () => {};
  }
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- shape checked above
  const PerformanceObserverClass = Observer as PerformanceObserverConstructor;
  const supported = PerformanceObserverClass.supportedEntryTypes ?? [];
  // Entries carry `performance.now()` times; convert once per batch to wall-clock timestamps.
  const toTimestamp = (startTime: number) => Math.round(Date.now() - performance.now() + startTime);
  const observers: PerformanceObserverShape[] = [];
  if (supported.includes("event")) {
    const observer = new PerformanceObserverClass((list) => {
      for (const entry of list.getEntries()) {
        if (!INTERACTION_EVENT_NAMES.has(entry.name)) {
          continue;
        }
        const processingStart = entry.processingStart ?? entry.startTime;
        recordDiagnosticsEvent({
          durationMs: Math.round(entry.duration),
          inputDelayMs: Math.round(processingStart - entry.startTime),
          interactionId: entry.interactionId ?? 0,
          kind: "interaction",
          name: entry.name,
          processingMs: Math.round((entry.processingEnd ?? processingStart) - processingStart),
          route: getDiagnosticsRoute(),
          timestamp: toTimestamp(entry.startTime),
        });
      }
    });
    observer.observe({ durationThreshold: INTERACTION_DURATION_THRESHOLD_MS, type: "event" });
    observers.push(observer);
  }
  if (supported.includes("longtask")) {
    const observer = new PerformanceObserverClass((list) => {
      for (const entry of list.getEntries()) {
        recordDiagnosticsEvent({
          durationMs: Math.round(entry.duration),
          kind: "long-task",
          route: getDiagnosticsRoute(),
          timestamp: toTimestamp(entry.startTime),
        });
      }
    });
    observer.observe({ type: "longtask" });
    observers.push(observer);
  }
  return () => {
    for (const observer of observers) {
      observer.disconnect();
    }
  };
}

function flushSessionFile(): void {
  if (Platform.OS === "web" || !isDiagnosticsRecording()) {
    return;
  }
  try {
    writeDiagnosticsSessionFile(snapshotDiagnostics(SESSION_FILE_LIMITS));
  } catch {
    // Disk persistence is best-effort; the in-memory buffers are the source of truth.
  }
}

function installSessionFlush(): () => void {
  const state = getRecorderState();
  if (!state.hasRotatedSessionFile) {
    state.hasRotatedSessionFile = true;
    try {
      rotateDiagnosticsSessionFile();
    } catch {
      // A missing / unwritable cache directory just means no previous-session evidence.
    }
  }
  const timer = setInterval(flushSessionFile, SESSION_FLUSH_INTERVAL_MS);
  return () => {
    clearInterval(timer);
    flushSessionFile();
  };
}

/**
 * Starts capturing logs, resource samples, stalls, errors, memory warnings, navigation and
 * Sentry events into the diagnostics buffers. Idempotent. Every capture is installed inside its
 * own `try` so one unavailable source (no native module, no `ErrorUtils` on web) never blocks
 * the rest.
 */
export function startDiagnosticsRecorder(): void {
  const state = getRecorderState();
  if (state.isActive) {
    return;
  }
  state.isActive = true;
  markDiagnosticsRecording(true);
  const installers: (() => () => void)[] = [
    () => subscribeToCapturedLogs(recordDiagnosticsLog),
    installGlobalErrorCapture,
    installUnhandledRejectionCapture,
    installAppStateCapture,
    installNetworkCapture,
    installNativeMonitoring,
    installSentryCapture,
    installSampler,
    installStallProbe,
    installPerformanceTimelineCapture,
    installSessionFlush,
  ];
  for (const install of installers) {
    try {
      state.disposers.push(install());
    } catch {
      // Skip a capture source that fails to install.
    }
  }
}

export function stopDiagnosticsRecorder(): void {
  const state = getRecorderState();
  if (!state.isActive) {
    return;
  }
  // Disposers persist the final session file, which checks the recording flag — so tear down
  // BEFORE flipping it.
  for (const dispose of state.disposers.reverse()) {
    try {
      dispose();
    } catch {
      // Best-effort teardown.
    }
  }
  state.disposers = [];
  state.isActive = false;
  markDiagnosticsRecording(false);
}

/** Drops a labelled marker into the event stream (e.g. "user tapped Send report"). */
export function markDiagnostics(label: string): void {
  recordDiagnosticsEvent({ kind: "marker", label, timestamp: Date.now() });
}
